import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import {
  IChangeEventStatus,
  ICreateEvent,
  IEventListItem,
  IEventListParams,
  IEventListResponse,
  IEventResponse,
  IUpdateEvent,
  ISettleEvent,
} from './types';

interface IEventsOptions {
  params?: IEventListParams;
  enabled?: boolean;
  retry?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}

export const useGetEvents = (params?: IEventListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.Page,
    params?.ItemCount
  );

  return useInfiniteQuery<IEventListItem[], DefaultError>({
    queryKey: ['events', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Event/Search`,
        params: { ...params, ItemCount: itemCount, Page: pageParam },
      });

      const events = await responseHandler<IEventListResponse>(response, {
        groupName: `useGetEvents - ${pageParam}`,
      });

      return infiniteHandler<IEventListItem>(events);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetEvent = (
  id: string,
  options?: IResponseHandlerOptions<IEventResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEventResponse, DefaultError>({
    queryKey: ['event', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Event/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetEvent - ${id}`,
        notFoundPath: '/events',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const useCreateEvent = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('createEvent');

  return useMutation<IEventResponse, DefaultError, ICreateEvent>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: body.eventName,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Event`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `createEvent`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateEvent = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('updateEvent');

  return useMutation<unknown, DefaultError, IUpdateEvent>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: body.eventName,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Event`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `updateEvent`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('event.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useChangeEventStatus = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('changeEventStatus');

  return useMutation<unknown, DefaultError, IChangeEventStatus>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: i18next.t('event.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Event/ChangeStatus`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `changeEventStatus`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('event.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useSettleEvent = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('settleEvent');

  return useMutation<unknown, DefaultError, ISettleEvent>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.settle.loading', {
          what: i18next.t('event.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Event/Settle`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `settleEvent`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.settle.success', {
          what: i18next.t('event.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteEvent = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('deleteEvent');

  return useMutation<unknown, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-code.remove.loading', {
          what: i18next.t('event.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Event/${id}`,
      });

      return responseHandler(response, {
        groupName: `deleteEvent`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('event.what'),
        })
      );
    },
    onError: dismiss,
  });
};
